import { useCallback } from "react";

import { type UseQueryResult, useQueries, queryOptions } from "@tanstack/react-query";
import { type RawParams, useCurrentStateAndParams } from "@uirouter/react";
import dayjs from "dayjs";

import { trafficSourcesForSegments } from "app/Shared/utils";
import { type TimeGranularity, resolveTimeGranularity } from "app/components/widget/widget-utilities/time-granularity";
import type { ICategoryComparison } from "app/pages/industry-analysis/categories-context/CategoriesContext";
import durationService from "app/services/DurationService";
import { DefaultFetchService } from "app/services/fetchService";
// fallow-ignore-next-line boundary-violation -- shared Lines of Business util lives in segments zone; move to app-shared pending
import { hasLinesOfBusinessSegments } from "app/services/segments/LinesOfBusinessUtils";

interface IMarketingChannelsQueryParams {
    category: string;
    categoryHash: string;
    keys: string;
    country: string;
    from: string;
    to: string;
    webSource: string;
    isWindow: boolean;
    timeGranularity: TimeGranularity;
    includeSubdomains: boolean;
}

type DateTime = string;

type Channel = string;

interface IMarketMarketingChannelsResponse {
    Totals: Partial<Record<Channel, number>>;
    Graph: Partial<Record<Channel, Record<DateTime, number>>>;
}

const sum = (operandA: number, operandB: number) => {
    return operandA + operandB;
};

const sortDateEntries = (entries: [string, number][]) => {
    return entries.toSorted(([dateA], [dateB]) => dayjs(dateA).valueOf() - dayjs(dateB).valueOf());
};

const sortGraphData = (graph: Partial<Record<Channel, Record<DateTime, number>>>) => {
    const sortedGraph: Partial<Record<Channel, Record<DateTime, number>>> = {};

    Object.entries(graph).forEach(([channel, timeSeriesData]) => {
        if (timeSeriesData && typeof timeSeriesData === "object") {
            // keys are not guaranteed to be in chronological order, so we have to sort them
            const sortedEntries = sortDateEntries(Object.entries(timeSeriesData));
            sortedGraph[channel] = Object.fromEntries(sortedEntries);
        }
    });

    return sortedGraph;
};

// The shared traffic-sources map labels the search channels as plain "Organic"/"Paid",
// but every other marketing-channels chart shows them as "Search - Organic"/"Search - Paid".
// Override by primary key (robust to the various raw channel spellings) to keep this page aligned.
const SEARCH_CHANNEL_TITLE_OVERRIDES: Record<string, string> = {
    /* eslint-disable @typescript-eslint/naming-convention -- keys are channel primary keys from the data model */
    "Organic Search": "utils.search.organic",
    "Paid Search": "utils.search.paid",
    /* eslint-enable @typescript-eslint/naming-convention */
};

const transformMarketingChannelsResponse = (response: IMarketMarketingChannelsResponse) => {
    const { Totals, Graph } = response;
    const total = Object.values(Totals).reduce(sum, 0);

    const channelVolumeList = Object.entries(Totals)
        .map(([channel, volume]) => {
            const trafficSource = trafficSourcesForSegments[channel as keyof typeof trafficSourcesForSegments];
            const primaryKey = trafficSource?.primaryKey;
            return {
                channel,
                volume,
                share: total ? volume / total : 0,
                priority: trafficSource?.priority ?? Number.MAX_SAFE_INTEGER,
                titleKey: (primaryKey && SEARCH_CHANNEL_TITLE_OVERRIDES[primaryKey]) || trafficSource?.title,
            };
        })
        .sort((left, right) => left.priority - right.priority);

    return {
        totals: Totals,
        graph: sortGraphData(Graph),
        total,
        channelVolumeList,
    };
};

export type MarketingChannelsData = ReturnType<typeof transformMarketingChannelsResponse>;

const ENDPOINT = "/api/Market/MarketingChannels/GetGraphData";

interface IUseMarketingChannelsQueriesParams {
    categories: ICategoryComparison[];
}

const getQueryParamsForCategory = (
    categoryComparison: ICategoryComparison,
    params: RawParams,
): IMarketingChannelsQueryParams => {
    const { country, webSource, duration, includeSubdomains = true } = params;
    const timeGranularity = resolveTimeGranularity(duration);
    const durationData = durationService.getDurationData(duration);

    return {
        category: categoryComparison.category.forApi,
        categoryHash: categoryComparison.category.isCustomCategory ? categoryComparison.category.categoryHash : "",
        keys: categoryComparison.category.forApi,
        from: durationData.forAPI.from,
        to: durationData.forAPI.to,
        webSource: webSource as string,
        isWindow: durationData.forAPI.isWindow,
        country,
        timeGranularity,
        includeSubdomains,
    };
};

export const useMarketingChannelsQueries = ({ categories }: IUseMarketingChannelsQueriesParams) => {
    const { params } = useCurrentStateAndParams();
    const fetchService = DefaultFetchService.getInstance();

    const queries = categories.map((category) => {
        const queryParams = getQueryParamsForCategory(category, params);
        return queryOptions({
            queryKey: [ENDPOINT, queryParams],
            // MMX cannot be computed for custom industries that contain a Line of Business (DRI-10608),
            // so skip the API call entirely for those industries.
            enabled: !hasLinesOfBusinessSegments(category.category.segments),
            queryFn: ({ signal }: { signal: AbortSignal }) =>
                fetchService.get(ENDPOINT, queryParams, { cancellation: signal }),
        });
    });

    const combine = useCallback(
        (results: UseQueryResult<IMarketMarketingChannelsResponse>[]) => {
            return {
                data: Object.fromEntries(
                    results.map((result, index) => [
                        categories[index].id,
                        result.data ? transformMarketingChannelsResponse(result.data) : null,
                    ]),
                ),
                isLoading: results.some((result) => result.isLoading),
                isError: results.some((result) => result.isError),
            };
        },
        [categories],
    );

    return useQueries({ queries, combine });
};
